/**
 * Message Agent Types
 *
 * Foundational types for the messaging agent subsystem.
 * Architecture: two-layer — agent-level config (AI, tools, KB, CRM) + channel adapters.
 */

import type { AgentRagSettings } from '@/lib/agents/shared/types'
import type { ConfigSource } from '@/lib/ai/langchain/config-source'
import type { CrmSchemaSnapshot } from '@/lib/ai/tools/crm-opportunity/snapshot'
import type { ScheduleCallbackConfig } from '@/lib/ai/tools/schedule-callback'
import type { CalendarBookingConfig } from '@/lib/calendar'
import type { HandoverConfig } from '@/lib/ai/tools/human-handover/handover-config'
import type { DeliveryToolSettings } from '@/lib/deliveries/config'
import type { FollowUpSource, FollowUpStatus } from './follow-ups/constants'

// ---------------------------------------------------------------------------
// Enums / Union Types
// ---------------------------------------------------------------------------

export type MessageAgentStatus = 'draft' | 'active' | 'disabled';

export type ChannelType = 'instagram' | 'facebook' | 'telegram' | 'whatsapp' | 'tiktok' | 'web_widget' | 'web_demo';

// Typed as Record<ChannelType, string> (not Record<string, string>) so that a
// future ChannelType extension fails typecheck here instead of silently
// rendering a raw slug in channel settings / CRM bindings / lead titles.
export const CHANNEL_LABELS: Record<ChannelType, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  tiktok: 'TikTok',
  web_widget: 'Web Widget',
  web_demo: 'Web Demo',
};

export type ChannelStatus = 'connected' | 'disconnected' | 'error' | 'token_expired';

export type MessageDirection = 'inbound' | 'outbound' | 'system';

/**
 * Provenance of a stored message. Meaningful for non-inbound rows; drives the CRM-chat mirror
 * dedup (skip re-pushing `crm` rows) and observability slicing. Pairs with `direction`.
 */
export type MessageOrigin = 'ai' | 'operator_ui' | 'crm' | 'system';

export type MessageType =
  | 'text'
  | 'image'
  | 'video'
  | 'audio'
  | 'file'
  | 'quick_reply'
  | 'card'
  | 'postback'
  | 'reaction'
  | 'story_reply'
  | 'session_start'
  | 'reply_skipped'
  /** A handover escalation that actually dispatched an alert to the team. */
  | 'handover_notified'
  | 'send_failed';

export type ConversationStatus = 'active' | 'closed' | 'archived';

/**
 * Pause metadata stored in `message_conversations.metadata.ai_pause`.
 * Written by the handover tool, operator echo/CRM reply paths, the contact
 * exclusion pipeline, or manual pause UI. Cleared on unpause.
 */
export interface ConversationPauseMetadata {
  by: 'user' | 'ai_handover' | 'operator_echo' | 'crm_operator_reply' | 'contact_exclusion';
  reason?: string;
  /** message_messages.id (UUID) that triggered the pause */
  message_id?: string;
}

/** Operator echo message detected from webhook (human replied from native platform UI) */
export interface OperatorEchoMessage {
  /** The customer's platform-scoped ID (PSID for Facebook, IGSID for Instagram) */
  senderId: string;
  text: string | null;
  /** Attachment URL when the operator replied with media (image/video/audio) and no caption. */
  mediaUrl?: string | null;
  /** Renderable media kind for the attachment, or null when not a displayable media type. */
  mediaType?: 'image' | 'video' | 'audio' | null;
  /** Raw provider attachment type; names what the stored placeholder stands for. */
  attachmentType?: string | null;
  externalMessageId: string;
  timestamp: Date;
}

/** A customer removed a message they had already sent; it names the stored row, it is not a new one. */
export interface CustomerMessageDeletion {
  /** The customer's platform-scoped ID */
  senderId: string;
  externalMessageId: string;
  timestamp: Date;
}

/**
 * Captured customer PII stored in `message_conversations.metadata.customer_info`.
 * Written by capture_customer_info tool during conversation.
 * Shared shape — usable by any agent type.
 */
export interface CapturedCustomerInfo {
  name?: string;
  email?: string;
  phone?: string;
  /** ISO timestamp of last update */
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Session Tracking
// ---------------------------------------------------------------------------

/**
 * Persisted in `message_conversations.metadata.session` (renamed from `crm_session`).
 * Tracks session lifecycle for CRM integration — one opportunity per session.
 */
export interface SessionMetadata {
  /** ISO timestamp when this session started */
  session_started_at: string;
  /** Incrementing counter (1-based) */
  session_number: number;
  /**
   * Deferred session rollover. Set when a session-timeout gap is detected while
   * rollover is suppressed by a handover pause; holds the timestamp of the first
   * post-gap inbound (= where the new session should start). Consumed (via the
   * metadata.session overwrite) by the next unsuppressed inbound or by lifting the
   * pause. Without this, the gap evidence is destroyed the moment the paused
   * inbound bumps last_message_at, freezing the session forever.
   */
  pending_rollover_at?: string;
  /** CRM source_id for this session: "{conversationId}:s{N}" */
  source_id?: string;
  /** crm_links.id for this session's lead */
  crm_link_id?: string;
  /**
   * Provider-native opportunity id for this session's lead. Denormalized
   * from `crm_links.crm_entity_id` so the AI reply path can pin the CRM
   * opportunity tools to it without an extra DB lookup per turn. Written
   * alongside `source_id` / `crm_link_id` by `syncSessionToCrm`.
   */
  crm_opportunity_id?: string;
  /** LLM-generated summary of previous session (cached for next session's first note) */
  prev_session_summary?: string;
  /** External CRM URL of previous session's lead */
  prev_lead_url?: string;
}

/**
 * In-memory context threaded through the pipeline for the current session.
 *
 * Why separate from SessionMetadata: after msgPatchConversationMetadata() writes to DB,
 * the in-memory `conversation` object is stale. SessionContext is the single
 * source of truth during pipeline execution — no DB refetch needed.
 */
export interface SessionContext {
  sessionNumber: number;
  sessionStartedAt: string;
  isNewSession: boolean;
  /** From metadata or freshly generated */
  prevSessionSummary?: string;
  /** External CRM URL of previous session's lead */
  prevLeadUrl?: string;
  /** "{conversationId}:s{N}" — set after CRM creates opportunity */
  sourceId?: string;
  /** crm_links.id — set after CRM creates opportunity */
  crmLinkId?: string;
  /**
   * Provider-native opportunity id — set after CRM creates / recovers
   * the opportunity. Threaded through to the AI reply path so the CRM
   * opportunity tools can pin to a specific lead via `ctx.crm.opportunityId`.
   */
  crmOpportunityId?: string;
  // Phase 5: Previous session info for close note (set only when isNewSession)
  /** Previous session's source_id (for finding CRM link) */
  prevSourceId?: string;
  /** Previous session number */
  prevSessionNumber?: number;
  /** Previous session start time (lower bound for message loading) */
  prevSessionStartedAt?: string;
  /** Last message timestamp before this session (upper bound for message loading) */
  prevLastMessageAt?: string;
}

/**
 * The System Prompt route's GET body, and its 409's: the text the editor shows, where it comes
 * from, and the hash a save names as its base (null while the text is inherited).
 */
export interface AgentPromptResponse {
  system_prompt: string;
  source: ConfigSource | 'none';
  prompt_hash: string | null;
}

// ---------------------------------------------------------------------------
// Agent Config (JSONB stored in message_agents.config)
// ---------------------------------------------------------------------------

/** Supabase select columns for message_channels — public fields (no credentials) */
export const CHANNEL_PUBLIC_COLUMNS = 'id, message_agent_id, channel_type, account_name, external_account_id, status, webhook_verified, last_error, connected_at, created_at, config, ai_replies_enabled' as const

/**
 * Safe config keys exposed to client — strips oauth_nonce and other internal fields.
 * Keys are additive across channels; unused keys are harmlessly ignored.
 */
const CHANNEL_CONFIG_PUBLIC_KEYS = new Set([
  'profile_picture_url',
  'display_name',
  'offline_message',
  // WhatsApp: drives the Coexistence badge and the support path (no re-register,
  // no PIN). `waba_id` sits beside it in config and stays internal.
  'coexistence',
  // web_widget config keys
  'primaryColor',
  'position',
  'welcomeMessage',
  'title',
  'avatarUrl',
  'showBranding',
  'borderRadius',
  'bubbleStyle',
  'headerStyle',
  'headerSubtitle',
  'bubbleAnimation',
  'bubbleAnimationDelay',
  'fontFamily',
  'fontSize',
  'bubbleOffsetX',
  'bubbleOffsetY',
  'bubbleSize',
  'brandingText',
  'typingIndicatorDelay',
  'siteUrl',
  // web_demo config keys
  'skip_domain_validation',
  'allowed_origins',
  'rate_limits',
])

/** Strip internal fields from channel config before sending to client */
export function sanitizeChannelConfig(config: Record<string, unknown> | null | undefined): Record<string, unknown> | undefined {
  if (!config) return undefined
  const safe: Record<string, unknown> = {}
  for (const key of CHANNEL_CONFIG_PUBLIC_KEYS) {
    if (key in config) safe[key] = config[key]
  }
  return Object.keys(safe).length > 0 ? safe : undefined
}

/**
 * Normalize a user-supplied URL to a safe absolute http(s) URL, or null when it
 * isn't one. Single source of truth for both `validateSiteUrl` (write path) and
 * `getChannelExternalUrl` (render path) so they never disagree.
 *
 * The scheme may be omitted ("example.com" → "https://example.com"). An explicit
 * non-http(s) scheme (ftp:, javascript:, mailto:, …) is rejected — we only
 * prepend `https://` when there is NO scheme, never on top of one (otherwise
 * "ftp://x" would become the bogus-but-parseable "https://ftp://x"). The scheme
 * candidate must not contain a dot, so `host:port` ("example.com:8080") is
 * correctly treated as schemeless rather than as a scheme named "example.com".
 */
export function normalizeHttpUrl(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const schemeMatch = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+-]*):/)
  let candidate: string
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase()
    if (scheme !== 'http' && scheme !== 'https') return null
    candidate = trimmed
  } else {
    candidate = `https://${trimmed}`
  }
  try {
    const parsed = new URL(candidate)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
  } catch {
    return null
  }
  return candidate
}

/**
 * Validate the operator-supplied `siteUrl` for web_widget / web_demo configs.
 * Empty/undefined is allowed (the field is optional). A non-empty value must
 * normalize to an http(s) URL via {@link normalizeHttpUrl}. Returns an error
 * string or null.
 */
export function validateSiteUrl(value: unknown): string | null {
  if (value === undefined) return null
  if (typeof value !== 'string' || value.length > 200) {
    return 'siteUrl must be a string of at most 200 characters'
  }
  if (value.trim() === '') return null
  if (normalizeHttpUrl(value) === null) {
    return 'siteUrl must be a valid http(s) URL (e.g. https://example.com)'
  }
  return null
}

/** Default values for reply config — single source of truth for code and UI */
export const REPLY_DEFAULTS = {
  max_conversation_history: 20,
  session_timeout_minutes: 1440, // 24h
  debounce_seconds: 8,
  auto_pause_on_human: true,
  max_chunks: 6,
  context_bridge: {
    // On the first inbound of a NEW session, surface the prior session's unanswered outbound
    // outreach to the AI (so a delayed customer reply isn't answered blind). See
    // docs/MESSAGE_AGENTS.md and lib/agents/messaging/core/conversation-service.ts:loadPriorOutreachContext.
    enabled: true,
    max_age_days: 7, // don't resurrect outreach older than this (one week)
    max_messages: 10, // cap on bridged prior messages
  },
} as const

export const MAX_SESSION_TIMEOUT_HOURS = 720

/** The agent's session bound. `0` disables it; absent means the platform default; a longer stored one counts as the cap. */
export function resolveSessionTimeoutMin(config: MessageAgentConfig | null | undefined): number {
  const stored = config?.reply?.session_timeout_minutes ?? REPLY_DEFAULTS.session_timeout_minutes
  return Math.min(stored, MAX_SESSION_TIMEOUT_HOURS * 60)
}

// ---------------------------------------------------------------------------
// Follow-ups (stored in message_agents.config.follow_ups)
// ---------------------------------------------------------------------------

/** One step of a follow-up chain. Delays are minutes throughout — the UI converts. */
export interface FollowUpStep {
  delay_minutes: number;
  /** `ai` decides at fire time whether a nudge is appropriate and writes it; `fixed` sends `text` verbatim. */
  mode: 'ai' | 'fixed';
  text?: string;
}

export interface FollowUpsConfig {
  enabled: boolean;
  steps: FollowUpStep[];
  /**
   * Channels the chain runs on. Absent means the default below — read it through
   * {@link resolveFollowUpChannels}, never directly, so "unset" cannot mean one
   * thing here and another there.
   */
  channels?: ChannelType[];
}

/**
 * `web_demo` is excluded by default: a demo visitor is an anonymous passer-by who
 * goes quiet by definition, and every armed chain costs a billed smart-check call
 * nobody will read.
 */
export const FOLLOW_UP_DEFAULT_CHANNELS: readonly ChannelType[] = (
  Object.keys(CHANNEL_LABELS) as ChannelType[]
).filter((type) => type !== 'web_demo');

export function resolveFollowUpChannels(config: FollowUpsConfig | null | undefined): ChannelType[] {
  return config?.channels ?? [...FOLLOW_UP_DEFAULT_CHANNELS];
}

/**
 * Defaults + bounds — single source of truth for code, migration and UI.
 * The second step is 1200 minutes (20h) rather than a round 24h on purpose: it
 * has to stay inside Meta's 24h messaging window, which is measured from the
 * customer's last message, not the agent's.
 */
export const FOLLOW_UP_DEFAULTS: {
  enabled: boolean
  steps: readonly FollowUpStep[]
  MAX_STEPS: number
  MIN_DELAY_MINUTES: number
  MAX_DELAY_MINUTES: number
  MAX_TEXT_LENGTH: number
} = {
  enabled: false,
  steps: [
    { delay_minutes: 60, mode: 'ai' },
    { delay_minutes: 1200, mode: 'ai' },
  ],
  MAX_STEPS: 3,
  /** Below this a follow-up competes with debounce and nudges a customer who is simply typing slowly. */
  MIN_DELAY_MINUTES: 2,
  /** 30 days. */
  MAX_DELAY_MINUTES: 43200,
  MAX_TEXT_LENGTH: 1000,
} as const

/**
 * Validate a `follow_ups` config block. Returns an error string or null.
 * The server is the final authority — the UI mirrors these rules, it does not
 * replace them.
 */
export function validateFollowUpsConfig(value: unknown): string | null {
  if (value === undefined) return null
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return 'follow_ups must be an object'
  }

  const block = value as Record<string, unknown>

  if (typeof block.enabled !== 'boolean') {
    return 'follow_ups.enabled must be a boolean'
  }

  if (!Array.isArray(block.steps)) {
    return 'follow_ups.steps must be an array'
  }
  if (block.steps.length > FOLLOW_UP_DEFAULTS.MAX_STEPS) {
    return `follow_ups.steps must contain at most ${FOLLOW_UP_DEFAULTS.MAX_STEPS} steps`
  }

  if (block.channels !== undefined) {
    if (!Array.isArray(block.channels)) {
      return 'follow_ups.channels must be an array'
    }
    // hasOwn, not `in`: `in` walks the prototype and would accept 'toString'.
    const unknown = block.channels.find(
      (c) => typeof c !== 'string' || !Object.hasOwn(CHANNEL_LABELS, c),
    )
    if (unknown !== undefined) {
      return `follow_ups.channels contains an unknown channel: ${String(unknown)}`
    }
    if (block.enabled === true && block.channels.length === 0) {
      return 'follow_ups.channels must contain at least one channel while follow-ups are enabled'
    }
  }

  for (let i = 0; i < block.steps.length; i++) {
    const step = block.steps[i] as Record<string, unknown> | null
    const at = `follow_ups.steps[${i}]`

    if (typeof step !== 'object' || step === null || Array.isArray(step)) {
      return `${at} must be an object`
    }

    const delay = step.delay_minutes
    if (typeof delay !== 'number' || !Number.isInteger(delay)) {
      return `${at}.delay_minutes must be an integer number of minutes`
    }
    if (delay < FOLLOW_UP_DEFAULTS.MIN_DELAY_MINUTES || delay > FOLLOW_UP_DEFAULTS.MAX_DELAY_MINUTES) {
      return `${at}.delay_minutes must be between ${FOLLOW_UP_DEFAULTS.MIN_DELAY_MINUTES} and ${FOLLOW_UP_DEFAULTS.MAX_DELAY_MINUTES}`
    }

    if (step.mode !== 'ai' && step.mode !== 'fixed') {
      return `${at}.mode must be 'ai' or 'fixed'`
    }

    if (step.mode === 'fixed') {
      if (typeof step.text !== 'string' || step.text.trim() === '') {
        return `${at}.text is required when mode is 'fixed'`
      }
      if (step.text.length > FOLLOW_UP_DEFAULTS.MAX_TEXT_LENGTH) {
        return `${at}.text must be at most ${FOLLOW_UP_DEFAULTS.MAX_TEXT_LENGTH} characters`
      }
    }
  }

  return null
}

// ---------------------------------------------------------------------------
// Per-channel offline message (stored under `message_channels.config.offline_message`)
// ---------------------------------------------------------------------------

/** Widget SHOWS the text, messengers SEND it once per session; defaults resolved at read time. */
export interface ChannelOfflineMessageConfig {
  enabled?: boolean;
  /** Unset = platform default. */
  text?: string;
}

export const OFFLINE_MESSAGE_MAX_LENGTH = 500;

/** An agent's own instructions; roles and composed prompts keep `ROLE_SYSTEM_PROMPT_MAX_LENGTH`. */
export const MESSAGE_AGENT_INSTRUCTIONS_MAX_LENGTH = 30000;

/** `null` = reject the request. Strips unknown keys. */
export function normalizeOfflineMessageConfig(value: unknown): ChannelOfflineMessageConfig | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const { enabled, text } = value as ChannelOfflineMessageConfig;
  if (enabled !== undefined && typeof enabled !== 'boolean') return null;
  if (text !== undefined && (typeof text !== 'string' || text.length > OFFLINE_MESSAGE_MAX_LENGTH)) {
    return null;
  }
  return {
    ...(enabled !== undefined ? { enabled } : {}),
    ...(text !== undefined ? { text } : {}),
  };
}

// ---------------------------------------------------------------------------
// Web Widget Config (stored in message_channels.config)
// ---------------------------------------------------------------------------

export interface WebWidgetConfig {
  primaryColor?: string;        // hex, default "#4F46E5"
  position?: 'bottom-right' | 'bottom-left';
  welcomeMessage?: string;
  title?: string;
  avatarUrl?: string;
  showBranding?: boolean;       // default: true
  borderRadius?: number;        // px, 0-40, default 12
  bubbleStyle?: 'filled' | 'outline'; // filled = solid bg, outline = white bg + colored border/icon
  headerStyle?: 'solid' | 'gradient'; // solid = flat, gradient = primaryColor → darker
  headerSubtitle?: string;      // e.g. "Usually replies in minutes"
  bubbleAnimation?: 'none' | 'bounce' | 'pulse' | 'wiggle'; // entrance animation, default 'bounce'
  bubbleAnimationDelay?: number; // seconds before animation plays, default 3
  fontFamily?: string;          // CSS font-family, default 'System' (system-ui stack)
  fontSize?: number;            // px, 12-18, default 14
  bubbleOffsetX?: number;       // px from side edge, see WIDGET_BUBBLE_OFFSET
  bubbleOffsetY?: number;       // px from bottom edge, see WIDGET_BUBBLE_OFFSET
  bubbleSize?: number;          // launcher diameter px, see WIDGET_BUBBLE_SIZE
  brandingText?: string;        // footer text, default "Powered by Versafy.ai" (editable on paid plans)
  typingIndicatorDelay?: number; // seconds before typing dots appear after send, default 4
  siteUrl?: string;             // public URL of the site where this widget is embedded — purely informational, gives operators a link back to the live channel (see getChannelExternalUrl)
  /** Channel-generic key (`config.offline_message`); typed here so widget UI reads it too. */
  offline_message?: ChannelOfflineMessageConfig;
}

// ---------------------------------------------------------------------------
// Web Demo Config (stored in message_channels.config for channel_type='web_demo')
// ---------------------------------------------------------------------------

/**
 * Rate limits for the demo channel. All fields optional; missing values fall
 * back to `WEB_DEMO_RATE_LIMIT_DEFAULTS`. Thresholds live in channel config so
 * super users can tune per-campaign without a code change.
 */
export interface WebDemoRateLimits {
  /** Max sessions per visitor IP per rolling hour. Default: 5 */
  ip_sessions_per_hour?: number;
  /** Max inbound messages per conversation per minute. Default: 20 */
  messages_per_minute_conversation?: number;
  /** Max inbound messages per channel per minute (global cap). Default: 50 */
  messages_per_minute_channel?: number;
}

/**
 * Config JSON for `message_channels.config` when `channel_type='web_demo'`.
 *
 * - `skip_domain_validation`: bypasses strict `*.myshopify.com` validation and
 *   the MCP tools/list probe so demo visitors can enter Shopify custom
 *   storefront domains. Outbound MCP calls still go through the shared
 *   SSRF-safe client that resolves DNS at request time and pins the request to
 *   the checked public address.
 * - `allowed_origins`: embed allowlist — restricts which parent page origins
 *   can load the demo widget iframe. Checked at page-render time via `Referer`
 *   header, NOT on API routes (API fetches from the iframe carry the iframe's
 *   own origin). `'*'` is allowed in preview envs only (never in prod).
 * - Widget appearance fields: reuses the existing `WebWidgetConfig` keys that
 *   are meaningful for inline chat (colors, header copy, avatar URL, typography,
 *   branding, typing delay). Floating-only bubble keys are ignored by the demo
 *   iframe but may be present harmlessly if copied from a widget config.
 * - `rate_limits`: see `WebDemoRateLimits`.
 */
export interface WebDemoChannelConfig extends Partial<WebWidgetConfig> {
  skip_domain_validation?: boolean;
  allowed_origins?: string[];
  rate_limits?: WebDemoRateLimits;
}

/**
 * Default rate limits applied when a field is missing from
 * `WebDemoChannelConfig.rate_limits`. Exported so API routes and the
 * adapter share a single source of truth.
 */
export const WEB_DEMO_RATE_LIMIT_DEFAULTS = {
  ip_sessions_per_hour: 5,
  messages_per_minute_conversation: 20,
  messages_per_minute_channel: 50,
} as const satisfies Required<WebDemoRateLimits>;

/**
 * Resolve a single rate-limit threshold from `channel.config.rate_limits`,
 * falling back to the default when the value is missing OR non-finite.
 * `??` alone is not enough: a NaN persisted from a malformed config would
 * pass through and silently disable the enforcement site (`count >= NaN`
 * is always false). Positive-finite check guards that.
 */
export function resolveWebDemoRateLimit(
  value: number | undefined | null,
  key: keyof typeof WEB_DEMO_RATE_LIMIT_DEFAULTS,
): number {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value
  }
  return WEB_DEMO_RATE_LIMIT_DEFAULTS[key]
}

/**
 * Key stored under `message_conversations.metadata` for the visitor-selected
 * Shopify storefront domain. Read fresh inside `generateReply` (debounce-safe)
 * — never captured in route-local closures.
 */
export const WEB_DEMO_METADATA_DOMAIN_KEY = 'shopify_demo_domain' as const;

// ---------------------------------------------------------------------------
// Agent Config (JSONB stored in message_agents.config)
// ---------------------------------------------------------------------------

/** Exclusion entry captured from a live conversation (inbox action). */
export interface ContactExclusionEntry {
  channel_type: ChannelType;
  external_user_id: string;
  /** Display-name snapshot at capture time, UI-only — matching ignores it. */
  label: string | null;
}

/** Where and whether the "customer is waiting in a paused conversation" alert goes. */
export interface PausedConversationAlertConfig {
  enabled?: boolean;
  /** Tenant notification channel instance ids; empty/absent = no alert. */
  notification_channel_ids?: string[];
}

/**
 * Authoritative normalization for the alert section — the reply path reads it on every paused
 * inbound, so a malformed value must never be stored. `null` = reject the request.
 */
export function normalizePausedConversationAlertConfig(
  value: unknown,
): PausedConversationAlertConfig | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const { enabled, notification_channel_ids: ids } = value as PausedConversationAlertConfig;
  if (enabled !== undefined && typeof enabled !== 'boolean') return null;
  if (ids !== undefined && (!Array.isArray(ids) || ids.some((id) => typeof id !== 'string'))) {
    return null;
  }
  return {
    enabled: enabled ?? false,
    notification_channel_ids: (ids ?? []).map((id) => id.trim()).filter(Boolean),
  };
}

export interface MessageAgentConfig {
  /** Applied agent role slugs (ai_use_case_roles); 'custom' is exclusive. */
  roles?: string[];
  /** Tenant-invented display name; only meaningful when roles === ['custom']. */
  role_label?: string | null;
  /**
   * AI exclusion list: matching inbounds never reach the LLM — the conversation
   * is paused (`by: 'contact_exclusion'`) and mirrored to CRM. Three entry kinds,
   * all stored normalized; matching rules live in
   * `lib/agents/messaging/contact-exclusions.ts`. Every write must carry the
   * complete object — the config route merges per-key, so a partial object
   * would wipe the sibling arrays.
   */
  contact_exclusions?: {
    /** Typed handles — match `contact_handle || contact_name` (Instagram + Telegram usernames). */
    usernames: string[];
    /** E.164 phones (`+<digits>`) — match phone-kind channels' `external_user_id` (WhatsApp). */
    phones?: string[];
    /** Per-contact entries added from the inbox — match `(channel_type, external_user_id)`. */
    contacts?: ContactExclusionEntry[];
  };
  reply?: {
    enabled: boolean;
    dry_run: boolean;
    use_ai_tools: boolean;
    /** Number of messages to include in AI prompt context (default 20) */
    max_conversation_history: number;
    /** Inactivity timeout in minutes — messages older than this are excluded from AI context (default 1440 = 24h, 0 = disabled) */
    session_timeout_minutes: number;
    /** Debounce delay in seconds. When > 0, waits for user to stop typing before replying. 0 = immediate reply (default). */
    debounce_seconds?: number;
    /** Auto-pause AI when a human operator replies from native platform UI (default: true) */
    auto_pause_on_human?: boolean;
    /**
     * Cross-session context bridge. On the first inbound of a NEW session, pull the prior session's
     * unanswered OPERATOR outreach into the AI prompt so a delayed customer reply (e.g. accepting an
     * offer days later) isn't answered without context. AI outbounds never trigger the bridge.
     * See REPLY_DEFAULTS.context_bridge.
     */
    context_bridge?: {
      /** Master switch (default: true). */
      enabled?: boolean;
      /** Skip bridging when the prior session is older than this (default: 7 = one week). */
      max_age_days?: number;
      /** Cap on the number of bridged prior messages (default: 10). */
      max_messages?: number;
    };
    /**
     * Max number of messages a single reply may be split into on channels with
     * a hard text limit (e.g. Instagram). Beyond this, the last chunk is
     * truncated with a marker. Default 4. See REPLY_DEFAULTS.
     */
    max_chunks?: number;
  };
  /**
   * Per-tool enabled state. Key = tool name, value = enabled.
   * Tools not present in the map are DISABLED by default — must be explicitly set to true.
   */
  tools_enabled?: Record<string, boolean>;
  /** Voice Agent and outbound number used when this agent schedules callbacks. */
  schedule_call?: Omit<ScheduleCallbackConfig, 'enabled'>;
  /** Target Email Agent and mailbox for a customer-requested email from this conversation. */
  send_email?: { enabled?: boolean; email_agent_id?: string; inbox_id?: string; respect_working_hours?: boolean };
  /** WhatsApp channel used for scheduling a reply to the current conversation. */
  send_whatsapp?: { enabled?: boolean; message_agent_id?: string; channel_id?: string; respect_working_hours?: boolean };
  /** Explicit per-agent Delivery master switch; absent defaults to disabled. */
  delivery_enabled?: boolean;
  /** Delivery tool gates; absent means all delivery tools use their enabled default. */
  delivery_tools?: DeliveryToolSettings;
  /**
   * Handover delivery config (selected notification channel ids). The master
   * tool-enable stays in `tools_enabled.human_handover`; this block only decides
   * where the handover alert is delivered. CRM enablement is separate
   * (`crm_bindings.config.handover`).
   */
  human_handover?: HandoverConfig;
  /**
   * Alert the tenant when a customer writes into a conversation whose AI is paused.
   * Off unless `enabled`, and silent with an empty channel selection.
   */
  paused_conversation_alert?: PausedConversationAlertConfig;
  /**
   * Alert the tenant when a customer writes on a channel whose `ai_replies_enabled` is off.
   * Same shape and semantics as `paused_conversation_alert` (and shares its per-session dedup
   * marker — one "someone is waiting" alert per session, whatever silenced the AI).
   */
  ai_off_alert?: PausedConversationAlertConfig;
  /**
   * Pre-configured Shopify shop domain for Storefront MCP tools
   * (search_shopify_catalog, search_shopify_policies). When set, this is the
   * source of truth and overrides any value the LLM passes. Leave undefined
   * (or null) to let the LLM / DB lookup decide per call.
   *
   * NOTE: `null` is the sentinel for "auto-detect" — JSON.stringify drops
   * undefined fields, so the UI must send `null` to clear a previously-set
   * value through the PATCH/merge endpoint.
   */
  shopify_shop_domain?: string | null;
  /** CRM opportunity tool whitelist settings (per-agent). */
  crm_tools?: {
    settings?: {
      crm_update_opportunity_fields?: {
        allowed_field_codes: string[];
      };
      crm_move_opportunity_stage?: {
        allowed_stage_codes: string[];
      };
    };
    /**
     * Stored CRM schema snapshot. Written by the Refresh-schema endpoint;
     * read by the resolver runtime and by the save-settings endpoint.
     * Runtime LLM turns MUST NOT fetch live CRM schema — this snapshot is
     * the single source of truth until the operator clicks Refresh again.
     */
    schema_snapshot?: CrmSchemaSnapshot;
  };
  /**
   * Calendar Booking — check availability + book meeting via Google Calendar.
   * The credential is an email inbox's Google OAuth token (no message-agent
   * Google connect), selected explicitly via `inbox_id` (voice-agent model).
   * Enable state lives in `tools_enabled` (both calendar tools toggled together).
   */
  calendar_booking?: Partial<CalendarBookingConfig> & {
    inbox_id?: string | null;
  };
  /**
   * Proactive follow-ups: the agent returns to a conversation the customer went
   * silent on. Every agent carries an explicit block (the migration backfills
   * one) — an absent key would be read as a default in one place and as "off"
   * in another. See FOLLOW_UP_DEFAULTS.
   */
  follow_ups?: FollowUpsConfig;
  /** Selected KB document IDs for this agent. [] = no KB */
  knowledge_bases?: string[];
  /** RAG retrieval settings for this agent */
  rag_settings?: AgentRagSettings;
}
/**
 * The agent-wide AI Reply switch. The reply pipeline skips on `!config.reply?.enabled`, so
 * anything but an explicit `true` — a legacy agent with no reply config included — answers nobody.
 */
export function isAiReplyEnabled(config: MessageAgentConfig | null | undefined): boolean {
  return config?.reply?.enabled === true;
}

// ---------------------------------------------------------------------------
// DB Row Types
// ---------------------------------------------------------------------------

export interface MessageAgent {
  id: string;
  tenant_id: string;
  created_by: string;
  name: string;
  status: MessageAgentStatus;
  config: MessageAgentConfig;
  created_at: string;
  updated_at: string;
}

export interface MessageChannel {
  id: string;
  tenant_id: string;
  message_agent_id: string;
  channel_type: ChannelType;
  account_name: string | null;
  external_account_id: string | null;
  credentials_encrypted: string | null;
  config: Record<string, unknown>;
  status: ChannelStatus;
  webhook_verified: boolean;
  last_error: string | null;
  connected_at: string | null;
  /** Gates AI replies and follow-ups only — transport and operator sends stay live. */
  ai_replies_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface MessageConversation {
  id: string;
  tenant_id: string;
  message_agent_id: string;
  channel_id: string;
  channel_type: ChannelType;
  external_conversation_id: string | null;
  external_user_id: string;
  contact_name: string | null;
  /** Stable platform handle (TG/IG username) — see NormalizedInboundMessage.senderUsername. */
  contact_handle: string | null;
  contact_avatar_url: string | null;
  status: ConversationStatus;
  ai_paused: boolean;
  /** Opts this one conversation out of `reply.auto_pause_on_human` at every intervention path. */
  auto_pause_exempt: boolean;
  last_message_at: string | null;
  metadata: Record<string, unknown>;
  /** Debounce: AI reply deferred until this timestamp. NULL = no pending debounce. */
  reply_after: string | null;
  /** Debounce lease: stamped on claim, cleared on terminal completion. Stale = worker died (recoverable). */
  reply_claimed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface MessageMessage {
  id: string;
  tenant_id: string;
  conversation_id: string;
  direction: MessageDirection;
  message_type: MessageType;
  content_text: string | null;
  content_media_url: string | null;
  content_payload: Record<string, unknown> | null;
  external_message_id: string | null;
  is_ai_generated: boolean;
  origin: MessageOrigin;
  ai_request_log_id: string | null;
  sent_at: string | null;
  /** Platform user behind an inbox reply. NULL for AI, CRM and native-app messages. */
  sent_by: string | null;
  delivered_at: string | null;
  read_at: string | null;
  created_at: string;
}

export interface FollowUpRow {
  id: string;
  tenant_id: string;
  conversation_id: string;
  message_agent_id: string;
  source: FollowUpSource;
  step_index: number;
  step_payload: FollowUpStep;
  fire_at: string;
  claimed_at: string | null;
  delivery_started_at: string | null;
  attempts: number;
  status: FollowUpStatus;
  skip_reason: string | null;
  message_message_id: string | null;
  created_at: string;
  /**
   * Since when "has anyone spoken?" is measured. Shared by the whole chain: a
   * per-row `created_at` would restart the anchor at every step and let an
   * inbound slip between a step's delivery and its successor's insert.
   */
  engagement_anchor_at: string;
}

export interface MessageAgentReply {
  id: string;
  tenant_id: string;
  message_message_id: string | null;
  message_agent_id: string;
  reply_text: string | null;
  sent_at: string | null;
  dry_run: boolean;
  escalated: boolean;
  escalated_reason: string | null;
  skipped: boolean;
  skip_reason: string | null;
  sentiment_score: number | null;
  can_close: boolean | null;
  classification_label: string | null;
  ai_request_log_id: string | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Normalized Message Format (channel-agnostic)
// ---------------------------------------------------------------------------

export type InboundMessageType =
  | 'text'
  | 'image'
  | 'video'
  | 'audio'
  | 'file'
  | 'postback'
  | 'reaction'
  | 'story_reply';

export type WidgetContextPrimitive = string | number | boolean | null;

export type WidgetContextValue =
  | WidgetContextPrimitive
  | WidgetContextValue[]
  | { [key: string]: WidgetContextValue };

/**
 * Application-defined reference data attached to a web-widget conversation.
 * The widget transport deliberately does not prescribe field names or a
 * customer/account schema.
 */
export type WidgetContextData = Record<string, WidgetContextValue>;

export interface WidgetConversationContext {
  /** Data supplied directly by the embedding page; useful but not authenticated. */
  host?: WidgetContextData | null;
  /** Data whose short-lived signature was verified by the widget API. */
  verified?: WidgetContextData | null;
}

export interface NormalizedInboundMessage {
  externalMessageId: string;
  /** Channel-scoped user ID (IGSID, PSID, telegram_user_id, etc.) */
  senderId: string;
  /**
   * Channel-scoped conversation/thread id, when it differs from the sender.
   * Private 1:1 channels omit it; Telegram groups (group chat id) and
   * Telegram Business (composite connection:chat) set it. The conversation
   * key is `chatId ?? senderId` — see `conversationExternalKey`.
   */
  chatId?: string;
  timestamp: Date;
  type: InboundMessageType;
  text?: string;
  mediaUrl?: string;
  /**
   * Raw provider attachment type for ANY attachment, mapped or not (`share`, `story_mention`,
   * `sticker`, …). Adapters report it instead of inventing text; the core names the stored
   * placeholder from it when the attachment kind is one it cannot draw.
   */
  attachmentType?: string | null;
  /** Postback payload */
  payload?: string;
  /**
   * Channel-specific extras persisted into `message_messages.content_payload`
   * (merged with `payload` by `storeInboundMessage`). Private-storage media
   * carries `{ file_name, file_size, mime_type, storage_path }` here —
   * `content_media_url` stays NULL for it (signed URLs are minted per-response
   * on read; a Telegram file URL embeds the bot token and must not persist).
   */
  contentPayload?: Record<string, unknown>;
  senderName?: string;
  /**
   * Stable platform handle (Telegram @username, Instagram username), distinct
   * from the display-oriented senderName. Persisted to
   * `message_conversations.contact_handle` for rename-proof exclusion matching.
   */
  senderUsername?: string;
  senderAvatarUrl?: string;
  /** Original platform payload for debugging */
  rawEvent: unknown;
}

export type OutboundMessageType = 'text' | 'image' | 'quick_replies' | 'card';

export interface QuickReply {
  title: string;
  payload: string;
}

export interface NormalizedOutboundMessage {
  type: OutboundMessageType;
  text?: string;
  mediaUrl?: string;
  quickReplies?: QuickReply[];
}

// ---------------------------------------------------------------------------
// Channel Adapter
// ---------------------------------------------------------------------------

export interface ChannelCapabilities {
  maxTextLength: number;
  /** Unit of `maxTextLength`. Instagram counts UTF-8 bytes; most others chars. */
  maxTextUnit?: 'bytes' | 'chars';
  supportsImages: boolean;
  supportsQuickReplies: boolean;
  supportsCarousel: boolean;
  /** Hours before messaging window expires. null = unlimited */
  messagingWindowHours: number | null;
}

export interface SendResult {
  success: boolean;
  externalMessageId?: string;
  error?: string;
}

export interface ChannelCredentials {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: string;
  pageId?: string;
  /** Channel-specific extra data */
  [key: string]: unknown;
}

/**
 * Context for `ChannelAdapter.resolveInboundMedia` — the resolved conversation
 * is required for the storage path, which is why the hook runs only after the
 * inbound row is stored (the store insert is the idempotency claim; media side
 * effects must never run for duplicate webhook deliveries).
 *
 * `supabase` is typed loosely to keep this module isomorphic (client
 * components import display types from here); adapters cast to their server
 * client type.
 */
export interface MediaResolutionContext {
  supabase: unknown;
  tenantId: string;
  channelId: string;
  conversationId: string;
}

export interface ChannelAdapter {
  channelType: ChannelType;

  // Webhook
  verifyWebhook(req: Request): Response | Promise<Response>;
  parseIncomingMessages(req: Request): Promise<NormalizedInboundMessage[]>;

  // Sending
  sendMessage(
    channel: MessageChannel,
    recipientId: string,
    message: NormalizedOutboundMessage
  ): Promise<SendResult>;

  // OAuth
  getAuthUrl(tenantId: string, redirectUri: string): string;
  handleAuthCallback(code: string, state: string): Promise<ChannelCredentials>;
  refreshToken(channel: MessageChannel): Promise<ChannelCredentials>;

  // Typing indicator (no-op on platforms without support)
  sendTypingIndicator(channel: MessageChannel, recipientId: string): Promise<void>;

  // Capabilities
  getCapabilities(): ChannelCapabilities;

  /**
   * Optional: download channel media to private storage and return the
   * `content_payload` extras (`file_name`, `file_size`, `mime_type`,
   * `storage_path`) for the stored row. Called by the core processor ONLY for
   * non-duplicate inbound messages, after the row is stored. Implementations
   * must degrade gracefully — a media failure must not break the message flow.
   */
  resolveInboundMedia?(
    channel: MessageChannel,
    message: NormalizedInboundMessage,
    ctx: MediaResolutionContext
  ): Promise<Record<string, unknown> | null>;
}
